import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import { Modal } from 'react-bootstrap';
import { useState } from 'react';
import Axios from 'axios';
import * as Yup from 'yup';
import ErrorMessages from '../../errorConfig';
import UserRegistrationComponent from './UserRegistrationComponent';
import PmsUrlConfig from '../../urlConfig';
import pmsConstants from '../../pmsConstants';
import { formatPhoneNumber, logout, setAuthToken, getPractice } from '../common';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddUserSchema = Yup.object().shape({
    firstname: Yup.string().required(ErrorMessages.FIRSTNAME_REQUIRED),
    lastname: Yup.string().required(ErrorMessages.LASTNAME_REQUIRED),
    password: Yup.string()
        .required(ErrorMessages.PASSWORD_REQUIRED)
        .min(8, ErrorMessages.PASSWORD_LENGTH),
    confPassword: Yup.string()
        .oneOf([Yup.ref('password')], ErrorMessages.PASSWORDS_DONT_MATCH),
});

export default function EditUserForm({ onClose }) {

    const dispatch = useDispatch();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const practiceDetails = useSelector(state => state.practiceDetails);

    const practice = practiceDetails.practice;

    const selectedUser = practiceDetails.selectedUser;
    console.log("practiceDetails:", practiceDetails);
    console.log("selectedUser:", selectedUser);
    const loginDetails = useSelector(state => state.loginDetails);
    const loggedInUser = loginDetails && loginDetails.loggedInUser ? loginDetails.loggedInUser : null;
    var token = "";
    if (loginDetails) {
        token = loginDetails.jwtToken;
    }

    const [currentActive, setCurrentActive] = useState(selectedUser ? selectedUser.active_user : true);

    const isSelf = loggedInUser && selectedUser && loggedInUser.username === selectedUser.username;

    // Check if user is a super user (original practice admin)
    const isSuperUser = selectedUser.super_user === true;

    // For super user, use email as username
    const initialUsername = isSuperUser ? selectedUser.email : selectedUser.username;

    // Check if delete button should be shown: logged-in user is practice admin, not editing themselves, user is active, and not super user
    const canDeleteUser = () => {
        if (!loggedInUser || !selectedUser) return false;
        const isPracticeAdmin = loggedInUser.user_type === pmsConstants.USER_TYPE_PRACTICE_STRING;
        const isNotSelf = loggedInUser.username !== selectedUser.username;
        const isActive = currentActive === true;
        console.log("selectedUser:", selectedUser.username, " loggedInUser:", loggedInUser.username, " isActive:", isActive, " isSuperUser:", isSuperUser);
        return isPracticeAdmin && isNotSelf && isActive && !isSuperUser;
    };

    return (
        <div className="w-full">

            <Formik
                initialValues={{
                    firstname: selectedUser.firstname,
                    lastname: selectedUser.lastname,
                    userEmail: selectedUser.email,
                    username: initialUsername,
                    phoneNo: formatPhoneNumber(selectedUser.phoneNum),
                    password: selectedUser.password,
                    confPassword: selectedUser.password,
                    user_type: `${selectedUser.user_type === pmsConstants.USER_TYPE_PRACTICE_STRING ? pmsConstants.USER_TYPE_PRACTICE_STRING : pmsConstants.USER_TYPE_STAFF}`,
                    active_user: selectedUser.active_user ? true : false
                }}
                validationSchema={AddUserSchema}
                onSubmit={values => {
                    console.log("ActiveUser=", values.active_user);
                    // same shape as initial values
                    var editUserUrl = PmsUrlConfig.BOS_EDIT_USER_URL
                    setAuthToken(token);

                    // For super user, set username to email address
                    const usernameValue = isSuperUser ? values.userEmail : values.username;

                    Axios.post(editUserUrl, {
                        _id: selectedUser._id,
                        firstname: values.firstname,
                        lastname: values.lastname,
                        email: values.userEmail,
                        username: usernameValue,
                        phoneNum: values.phoneNo.replace(/[^\d]/g, ''),
                        password: values.password,
                        password_confirm: values.password,
                        user_type: values.user_type,
                        active_user: (values.active_user)
                    }
                    ).then((response) => {
                        console.log('Response status:', response.status);
                        console.log('Response data:', response.data);
                        
                        if (response.status === 200) {
                            toast.success('User updated successfully!', {
                                position: "top-center",
                                autoClose: 3000,
                                hideProgressBar: true,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true
                            });
                            dispatch({ type: 'editUserSuccess' })
                            setCurrentActive(values.active_user)
                            getPractice(practice._id, dispatch, token);

                            // If editing the logged-in user, update their email in Redux state
                            if (isSelf) {
                                dispatch({
                                    type: 'setLoggedInUser',
                                    payload: {
                                        ...loggedInUser,
                                        email: values.userEmail,
                                        username: isSuperUser ? values.userEmail : values.username
                                    }
                                });
                            }

                            // Call onSuccess callback to close modal
                            if (onClose) {
                                onClose();
                            }
                        }
                        else if (response.status === 400) {
                            const errorMessage = response.data?.error_code?.description || ErrorMessages.SERVER_ERROR;
                            toast.error(errorMessage, {
                                position: "top-center",
                                autoClose: 5000,
                                hideProgressBar: true,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true
                            });
                            dispatch({
                                type: 'practiceUpdateError',
                                payload: errorMessage
                            })
                        }
                        else if (response.status === 401 || response.status === 403) {
                            logout();
                            //TODO: Redirect to login page
                        }
                    }).catch((error) => {
                        console.log('Error caught:', error);
                        console.log('Error response:', error.response);
                        console.log('Error status:', error.response?.status);
                        console.log('Error data:', error.response?.data);
                        
                        const errorMessage = error.response?.data?.error_code?.description || ErrorMessages.SERVER_ERROR;
                        toast.error(errorMessage, {
                            position: "top-center",
                            autoClose: 5000,
                            hideProgressBar: true,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true
                        });
                        
                        if (error.response.data !== null && error.response.data.error_code !== null) {
                            dispatch({
                                type: 'practiceUpdateError',
                                payload: errorMessage
                            })
                        }
                    })
                }}
            >
                {({ errors, touched, values, handleChange, setFieldValue, isSubmitting }) => (
                    <Form data-testid='login-form' className="w-full">
                        <UserRegistrationComponent
                            errors={errors}
                            touched={touched}
                            values={values}
                            handleChange={handleChange}
                            prefix={'staffSignup'}
                            editPage={true}
                            dispatch={dispatch}
                            setFieldValue={setFieldValue}
                            hideActiveSwitch={isSelf}
                            isSuperUser={isSuperUser}
                            disabled={false}
                        />

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mt-6">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-8 py-2.5 bg-[#0B6D76] hover:bg-[#0A5A62] text-white font-semibold text-sm rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0B6D76] focus:ring-offset-2 min-w-[120px]"
                            >
                                {isSubmitting ? "Saving.." : "Save"}
                            </button>
                            {canDeleteUser() && (
                                <button
                                    type="button"
                                    onClick={() => setShowDeleteModal(true)}
                                    className="px-8 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold text-sm rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 min-w-[120px]"
                                >
                                    Delete User
                                </button>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
            
            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className="border-b border-gray-200">
                    <Modal.Title className="text-xl font-semibold text-gray-900">Delete User</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-6">
                    <p className="text-gray-700">Are you sure you want to delete user <span className="font-semibold">{selectedUser && selectedUser.email}</span>? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer className="border-t border-gray-200">
                    <button 
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                        onClick={() => setShowDeleteModal(false)} 
                        disabled={isDeleting}
                    >
                        No
                    </button>
                    <button 
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        onClick={async () => {
                            try {
                                setIsDeleting(true);
                                const deleteUrl = PmsUrlConfig.BOS_DELETE_ASSOCIATE_URL
                                setAuthToken(token);
                                const resp = await Axios.delete(deleteUrl, { data: selectedUser });
                                if (resp.status === 200) {
                                    setShowDeleteModal(false);
                                    dispatch({ type: 'deleteUserSuccess' });
                                    getPractice(practice._id, dispatch, token);
                                    onClose();
                                } else {
                                    dispatch({ type: 'practiceUpdateError', payload: resp.data && resp.data.error_code ? resp.data.error_code.description : 'Failed to delete user' });
                                }
                            } catch (err) {
                                console.error('Delete user error', err);
                                if (err && err.response && err.response.data && err.response.data.error_code)
                                    dispatch({ type: 'practiceUpdateError', payload: err.response.data.error_code.description });
                                else
                                    dispatch({ type: 'practiceUpdateError', payload: 'Failed to delete user' });
                            } finally {
                                setIsDeleting(false);
                            }
                        }} 
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Yes'}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>

    );


}